/**
 * Design tab co-pilot — right-hand dock (Phase 2). A streaming chat panel
 * wired to the `aiChatStream` adapter (→ design:aiStreamStart). Carries the
 * active section as context via `composeSystemPrompt`, streams tokens for local
 * and cloud-proxy clients, supports cancel, and lets the user apply an
 * assistant reply straight into the active section.
 *
 * Self-contained React state, matching DesignWorkspace's Phase-0 approach
 * (the full Anvil store port is a later phase).
 */
import { useCallback, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { SearchCheck } from 'lucide-react';
import type { DesignProject } from '@volley/crucible-ipc-types';
import { MarkdownContent } from '../../components/MarkdownContent';
import { useCrucibleClient } from '../../transport/context.js';
import { aiChatStream, readLens } from './anvil-api';
import { composeSystemPrompt } from './prompts';
import { reviewQuestionToMarkdown, runDesignReview } from './lib/review-flow';

type ChatTurn = UserChatTurn | AssistantChatTurn;

interface UserChatTurn {
  role: 'user';
  content: string;
}

interface AssistantChatTurn {
  role: 'assistant';
  content: string;
  kind: 'reply' | 'review';
  recommendation?: string;
}

export interface CoPilotPanelProps {
  gameId: string;
  projectName: string | null;
  projectType: string | null;
  sectionTitle: string | null;
  sectionDoc: string | null;
  sectionBody: string;
  /** Whether the open section uses the item editor — sharpens the system prompt
   *  toward surgical, merge-by-name item edits. */
  sectionIsItems?: boolean;
  /** Full project — enables the conversational "Review design" run. */
  project?: DesignProject | null;
  /** Selected lens ids folded into the system prompt (TDD §10). */
  lensIds?: { id: string; kind: 'agent' | 'skill' }[];
  /** Replace the active section body with an assistant reply. */
  onApplyToSection?: (markdown: string) => void;
  /** Apply to the section, then open Sync design pre-filled with the reply. */
  onApplyAndSync?: (markdown: string) => void;
  /** Open the freeform revision flow with an actionable review recommendation. */
  onAdoptRecommendation?: (instruction: string) => void;
  /** Layout hook for the host rail (defaults to the standalone dock width). */
  className?: string;
}

export function CoPilotPanel({
  gameId,
  projectName,
  projectType,
  sectionTitle,
  sectionDoc,
  sectionBody,
  sectionIsItems = false,
  project = null,
  lensIds = [],
  onApplyToSection,
  onApplyAndSync,
  onAdoptRecommendation,
  className = 'w-80 shrink-0 border-l border-crucible-border',
}: CoPilotPanelProps): ReactElement {
  const client = useCrucibleClient();
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cancelRef = useRef<(() => void) | null>(null);

  /** Conversational adversarial review — pushbacks land as chat turns. */
  const runReview = useCallback(async () => {
    if (!project || reviewing || streaming) return;
    setError(null);
    setReviewing(true);
    setTurns((prev) => [
      ...prev,
      {
        role: 'assistant',
        content:
          `Starting an adversarial review of ${project.name} — ` +
          "I'll push back on anything underspecified.",
        kind: 'review',
      },
    ]);
    try {
      const questions = await runDesignReview(client, { gameId, project, lensIds });
      setTurns((prev) => [
        ...prev,
        ...questions.map(
          (q): ChatTurn => ({
            role: 'assistant',
            content: reviewQuestionToMarkdown(q),
            kind: 'review',
            recommendation: q.recommendation?.trim() ? q.recommendation.trim() : undefined,
          }),
        ),
      ]);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setReviewing(false);
    }
  }, [project, reviewing, streaming, gameId, lensIds, client]);

  const send = useCallback(async () => {
    const text = draft.trim();
    if (!text || streaming) return;
    setError(null);
    setDraft('');

    // Snapshot the conversation including this user turn for the API call.
    const history: ChatTurn[] = [...turns, { role: 'user', content: text }];
    setTurns(history);
    setStreaming(true);
    setStreamingText('');

    // Load selected lens bodies (best-effort) to fold into the prompt.
    const lensBodies: { name: string; description: string; body: string }[] = [];
    for (const { id, kind } of lensIds) {
      try {
        const full = await readLens(client, id, kind);
        lensBodies.push({ name: full.name, description: full.description, body: full.body });
      } catch {
        // skip unreadable lens
      }
    }

    const system = composeSystemPrompt({
      projectName,
      projectType,
      sectionTitle,
      sectionDoc,
      sectionBody,
      sectionIsItems,
      lensBodies,
      referenceBodies: [],
    });

    const { cancel, done } = aiChatStream(
      client,
      {
        gameId,
        system,
        messages: history.map(({ role, content }) => ({ role, content })),
      },
      {
        onDelta: (_t, _n) => {
          // Re-render with the full accumulated text each delta (the adapter
          // tracks accumulation; we mirror it for display).
          setStreamingText((prev) => prev + _t);
        },
      },
    );
    cancelRef.current = cancel;

    done
      .then((result) => {
        setTurns((prev) => [...prev, { role: 'assistant', content: result.text, kind: 'reply' }]);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setStreaming(false);
        setStreamingText('');
        cancelRef.current = null;
      });
  }, [
    draft,
    streaming,
    turns,
    gameId,
    projectName,
    projectType,
    sectionTitle,
    sectionDoc,
    sectionBody,
    sectionIsItems,
    lensIds,
    client,
  ]);

  const onCancel = useCallback(() => {
    cancelRef.current?.();
  }, []);

  return (
    <aside className={`flex min-h-0 flex-col ${className}`} data-testid="design-copilot">
      <header className="flex items-center justify-between gap-2 border-b border-crucible-border px-3 py-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-crucible-muted">
          Co-Designer
        </h2>
        <div className="flex min-w-0 items-center gap-1.5">
          {project && (
            <button
              type="button"
              onClick={() => void runReview()}
              disabled={reviewing || streaming}
              data-testid="copilot-review-button"
              title="Adversarial review of the design — runs as a conversation here"
              className="inline-flex shrink-0 items-center gap-1 rounded border border-crucible-border px-1.5 py-0.5 text-[11px] text-crucible-muted hover:bg-crucible-bg-elevated disabled:opacity-40"
            >
              <SearchCheck size={11} aria-hidden="true" />
              {reviewing ? 'Reviewing…' : 'Review design'}
            </button>
          )}
          <span
            className="truncate rounded-full border border-white/10 bg-white/[0.05] px-2 py-0.5 text-[10px] text-crucible-muted"
            title="The Co-Designer sees whatever you have open"
            data-testid="copilot-context-chip"
          >
            {sectionTitle ?? 'Whole design'}
          </span>
        </div>
      </header>

      <div className="flex-1 space-y-3 overflow-y-auto p-3" data-testid="design-copilot-messages">
        {turns.map((turn, i) => (
          <div key={i} className="text-crucible-fg">
            <div className="mb-0.5 text-[10px] uppercase tracking-wide text-crucible-muted">
              {turn.role === 'user' ? 'You' : 'Co-Designer'}
            </div>
            {turn.role === 'assistant' ? (
              <>
                <div className="rounded bg-crucible-bg-elevated p-2 text-sm">
                  <MarkdownContent content={turn.content} />
                </div>
                {turn.kind === 'reply' && (onApplyToSection || onApplyAndSync) && (
                  <div className="mt-1 flex gap-1.5">
                    {onApplyToSection && (
                      <button
                        type="button"
                        onClick={() => onApplyToSection(turn.content)}
                        title="Write this into the open section only"
                        className="rounded border border-crucible-border px-2 py-0.5 text-[11px] text-crucible-muted hover:bg-crucible-bg-elevated"
                      >
                        Apply to section
                      </button>
                    )}
                    {onApplyAndSync && (
                      <button
                        type="button"
                        onClick={() => onApplyAndSync(turn.content)}
                        data-testid="copilot-apply-and-sync"
                        title="Apply here, then map and apply ripple effects across the rest of the design"
                        className="rounded border border-crucible-accent/40 px-2 py-0.5 text-[11px] text-crucible-accent-hover hover:bg-crucible-bg-elevated"
                      >
                        Apply &amp; sync
                      </button>
                    )}
                  </div>
                )}
                {turn.kind === 'review' && turn.recommendation && onAdoptRecommendation && (
                  <div className="mt-1 flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => onAdoptRecommendation(turn.recommendation!)}
                      data-testid="copilot-adopt-recommendation"
                      title="Open Revise design with this recommendation; nothing is applied until you review the proposal"
                      className="rounded border border-crucible-accent/40 px-2 py-0.5 text-[11px] text-crucible-accent-hover hover:bg-crucible-bg-elevated"
                    >
                      Adopt recommendation
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="whitespace-pre-wrap text-sm">{turn.content}</div>
            )}
          </div>
        ))}

        {streaming && (
          <div data-testid="design-copilot-streaming">
            <div className="mb-0.5 text-[10px] uppercase tracking-wide text-crucible-muted">
              Co-Designer
            </div>
            <div className="rounded bg-crucible-bg-elevated p-2 text-sm">
              {streamingText ? (
                <MarkdownContent content={streamingText} />
              ) : (
                <span className="text-crucible-muted">Thinking…</span>
              )}
            </div>
          </div>
        )}

        {error && (
          <div
            className="rounded border border-red-500/40 p-2 text-xs text-red-400"
            data-testid="design-copilot-error"
          >
            {error}
          </div>
        )}
      </div>

      <footer className="border-t border-crucible-border p-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void send();
            }
          }}
          rows={2}
          placeholder="Ask the co-designer… (⌘/Ctrl+Enter)"
          className="w-full resize-none rounded border border-crucible-border bg-transparent p-2 text-sm text-crucible-fg outline-none placeholder:text-crucible-muted"
        />
        <div className="mt-1 flex justify-end gap-2">
          {streaming ? (
            <button
              type="button"
              onClick={onCancel}
              className="rounded border border-crucible-border px-3 py-1 text-xs text-crucible-muted hover:bg-crucible-bg-elevated"
            >
              Stop
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void send()}
              disabled={!draft.trim()}
              className="rounded bg-crucible-accent px-3 py-1 text-xs font-medium text-white disabled:opacity-40"
            >
              Send
            </button>
          )}
        </div>
      </footer>
    </aside>
  );
}
